import type { User } from '../types/User'

/**
 * Languages the assistant may speak/answer in. Restricted to the app's supported locales so the client
 * can't push an arbitrary value into the locked live config. Add entries here when the app adds locales.
 */

export type AssistantLanguage = {
  /** BCP-47 code sent to the Live API, e.g. 'en-US'. */
  code: string
  /** English name used inside the model's system instruction. */
  name: string
  /** App i18n locale this maps to, e.g. 'en'. */
  locale: string
  /** Endonym shown in the picker — language names are never translated. */
  nativeName: string
  /** Two-letter badge for the compact picker button. */
  shortLabel: string
}

const ASSISTANT_LANGUAGES: Record<string, AssistantLanguage> = {
  en: { code: 'en-US', name: 'English', locale: 'en', nativeName: 'English', shortLabel: 'EN' },
  sv: { code: 'sv-SE', name: 'Swedish', locale: 'sv', nativeName: 'Svenska', shortLabel: 'SV' }
}

export const DEFAULT_ASSISTANT_LANGUAGE: AssistantLanguage = ASSISTANT_LANGUAGES.en

/** Ordered list for the language picker; derived so the picker and the allowlist cannot drift. */
export const ARIA_SUPPORTED_LANGUAGES: AssistantLanguage[] = Object.values(ASSISTANT_LANGUAGES)

// Map a UI locale ('en', 'sv', 'en-US', …) to a supported assistant language; defaults to English.
export function normalizeAssistantLanguage(input?: string): AssistantLanguage {
  const base = (input || '').toLowerCase().split('-')[0]
  return ASSISTANT_LANGUAGES[base] ?? DEFAULT_ASSISTANT_LANGUAGE
}

/** The name of the language a person has chosen, e.g. "Swedish"; nothing when they have not chosen one. */
export const resolveUserLanguageName = (user: Pick<User, 'language'>): string | undefined =>
  user.language ? normalizeAssistantLanguage(user.language).name : undefined
